import Phaser from 'phaser';
import {
  getOrCreatePlayer, savePlayerProfile,
  getTopScores, getPlayerRank, countryFlag,
} from '../LeaderboardManager';
import { showBanner } from '../AdManager';

// ── Country list (ISO 3166-1 alpha-2) ─────────────────────────────────────────
const COUNTRIES = [
  ['AF','Afghanistan'],['AL','Albania'],['DZ','Algeria'],['AR','Argentina'],
  ['AU','Australia'],['AT','Austria'],['BD','Bangladesh'],['BE','Belgium'],
  ['BR','Brazil'],['BG','Bulgaria'],['CA','Canada'],['CL','Chile'],
  ['CN','China'],['CO','Colombia'],['HR','Croatia'],['CZ','Czech Republic'],
  ['DK','Denmark'],['EG','Egypt'],['FI','Finland'],['FR','France'],
  ['DE','Germany'],['GH','Ghana'],['GR','Greece'],['GT','Guatemala'],
  ['HU','Hungary'],['IN','India'],['ID','Indonesia'],['IQ','Iraq'],
  ['IE','Ireland'],['IL','Israel'],['IT','Italy'],['JP','Japan'],
  ['JO','Jordan'],['KZ','Kazakhstan'],['KE','Kenya'],['KR','South Korea'],
  ['KW','Kuwait'],['LB','Lebanon'],['LY','Libya'],['MY','Malaysia'],
  ['MX','Mexico'],['MA','Morocco'],['NL','Netherlands'],['NZ','New Zealand'],
  ['NG','Nigeria'],['NO','Norway'],['PK','Pakistan'],['PE','Peru'],
  ['PH','Philippines'],['PL','Poland'],['PT','Portugal'],['QA','Qatar'],
  ['RO','Romania'],['RU','Russia'],['SA','Saudi Arabia'],['RS','Serbia'],
  ['SG','Singapore'],['ZA','South Africa'],['ES','Spain'],['LK','Sri Lanka'],
  ['SE','Sweden'],['CH','Switzerland'],['TW','Taiwan'],['TH','Thailand'],
  ['TN','Tunisia'],['TR','Turkey'],['UA','Ukraine'],['AE','UAE'],
  ['GB','United Kingdom'],['US','United States'],['UZ','Uzbekistan'],
  ['VN','Vietnam'],['YE','Yemen'],['ZW','Zimbabwe'],
];

const FONT      = 'Georgia, "Times New Roman", serif';
const FONT_BOLD = 'Georgia, "Times New Roman", serif';

export default class LeaderboardScene extends Phaser.Scene {
  constructor() {
    super({ key: 'LeaderboardScene' });
    this._tab            = 'foods';
    this._rows           = [];
    this._playerUUID     = null;
    this._playerRank     = null;
    this._scrollY        = 0;
    this._maxScroll      = 0;
    this._rowContainer   = null;
    this._isDragging     = false;
    this._dragStartY     = 0;
    this._dragScrollY    = 0;
    this._onboardingOpen = false;
  }

  create() {
    showBanner();

    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    this._sd  = sd;
    this._dpr = dpr;
    this._W   = W;
    this._H   = H;

    // ── Cotton candy background (matches HomeScene) ────────────────
    const bg = this.add.graphics();
    bg.fillGradientStyle(0xf8f0ff, 0xebe8ff, 0xf0e8ff, 0xf8f0ff, 1);
    bg.fillRect(0, 0, W, H);

    const bloom = this.add.graphics();
    [
      { x: W * 0.15, y: H * 0.20, r: W * 0.55, col: 0xff9ec5, a: 0.11 },
      { x: W * 0.88, y: H * 0.15, r: W * 0.50, col: 0x99ccff, a: 0.10 },
      { x: W * 0.50, y: H * 0.68, r: W * 0.55, col: 0xcc99ff, a: 0.09 },
    ].forEach(({ x, y, r, col, a }) => {
      for (let i = 4; i >= 1; i--) {
        bloom.fillStyle(col, (a / 4) * i);
        bloom.fillCircle(x, y, r * (i / 4));
      }
    });

    // Subtle dark overlay so card pops
    this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.18).setDepth(1);

    // ── Card dimensions ───────────────────────────────────────────
    const cardW    = Math.min(360 * sd, W * 0.92);
    const cardX    = W / 2;
    const cardTopY = H * 0.06;
    const cardBotY = H * 0.92;
    const cardH    = cardBotY - cardTopY;
    const cardMidY = cardTopY + cardH / 2; // eslint-disable-line no-unused-vars
    const cardR    = 22 * sd;

    // Card shadow
    const shadowGfx = this.add.graphics().setDepth(2);
    shadowGfx.fillStyle(0x000000, 0.25);
    shadowGfx.fillRoundedRect(cardX - cardW / 2 + 4 * sd, cardTopY + 4 * sd, cardW, cardH, cardR);

    // Card body
    const cardGfx = this.add.graphics().setDepth(3);
    cardGfx.fillStyle(0xfcf8ff, 0.97);
    cardGfx.fillRoundedRect(cardX - cardW / 2, cardTopY, cardW, cardH, cardR);

    // Purple header strip
    cardGfx.fillStyle(0x9944cc, 1);
    cardGfx.fillRoundedRect(cardX - cardW / 2, cardTopY, cardW, 90 * sd, cardR);
    cardGfx.fillRect(cardX - cardW / 2, cardTopY + 30 * sd, cardW, 60 * sd);

    // Purple footer strip
    cardGfx.fillStyle(0x7733bb, 1);
    cardGfx.fillRoundedRect(cardX - cardW / 2, cardBotY - 52 * sd, cardW, 52 * sd, cardR);
    cardGfx.fillRect(cardX - cardW / 2, cardBotY - 52 * sd, cardW, 26 * sd);

    // Card border
    cardGfx.lineStyle(3 * dpr, 0xbb88ee, 1);
    cardGfx.strokeRoundedRect(cardX - cardW / 2, cardTopY, cardW, cardH, cardR);

    // ── Title ──────────────────────────────────────────────────────
    const titleGap = 38 * sd;
    this.add.text(cardX, cardTopY + titleGap, '🏆  Leaderboard', {
      fontSize:        `${20 * sd}px`,
      fontFamily:      FONT_BOLD,
      fontStyle:       'bold italic',
      color:           '#ffffff',
      stroke:          '#5511aa',
      strokeThickness: 4 * dpr,
    }).setOrigin(0.5).setDepth(4);

    // ── Tab buttons ───────────────────────────────────────────────
    const tabY  = cardTopY + titleGap * 2;
    const tabW  = Math.min(130 * sd, cardW * 0.40);
    const tabH  = 34 * sd;
    const tabGap = 10 * sd;
    const tab1X = cardX - tabGap / 2 - tabW / 2;
    const tab2X = cardX + tabGap / 2 + tabW / 2;

    this._tabGfx1 = this.add.graphics().setDepth(4);
    this._tabGfx2 = this.add.graphics().setDepth(4);
    this._tabTxt1 = this.add.text(tab1X, tabY, '🍔  Foods', {
      fontSize: `${12 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold', color: '#ffffff',
    }).setOrigin(0.5).setDepth(5);
    this._tabTxt2 = this.add.text(tab2X, tabY, '📏  Height', {
      fontSize: `${12 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold', color: '#ffffff',
    }).setOrigin(0.5).setDepth(5);

    const drawTabs = () => {
      const c1 = this._tab === 'foods'  ? 0x7722aa : 0xaa66dd;
      const c2 = this._tab === 'height' ? 0x7722aa : 0xaa66dd;
      this._tabGfx1.clear();
      this._tabGfx1.fillStyle(c1, 1);
      this._tabGfx1.fillRoundedRect(tab1X - tabW / 2, tabY - tabH / 2, tabW, tabH, tabH / 2);
      this._tabGfx2.clear();
      this._tabGfx2.fillStyle(c2, 1);
      this._tabGfx2.fillRoundedRect(tab2X - tabW / 2, tabY - tabH / 2, tabW, tabH, tabH / 2);
    };
    this._drawTabs = drawTabs;
    drawTabs();

    const tab1Hit = this.add.rectangle(tab1X, tabY, tabW, tabH).setInteractive({ useHandCursor: true }).setDepth(5);
    const tab2Hit = this.add.rectangle(tab2X, tabY, tabW, tabH).setInteractive({ useHandCursor: true }).setDepth(5);
    tab1Hit.on('pointerdown', () => { if (this._tab !== 'foods')  { this._tab = 'foods';  drawTabs(); this._loadScores(); } });
    tab2Hit.on('pointerdown', () => { if (this._tab !== 'height') { this._tab = 'height'; drawTabs(); this._loadScores(); } });

    // ── Column headers ────────────────────────────────────────────
    const headerY = cardTopY + titleGap * 2 + 36 * sd;
    const rowH    = 36 * sd;
    const padL    = cardX - cardW / 2 + 12 * sd;
    const padR    = cardX + cardW / 2 - 12 * sd;
    const rankX   = padL + 18 * sd;
    const nameX   = padL + cardW * 0.30;
    const flagX   = padL + cardW * 0.62;
    const scoreX  = padR - 10 * sd;

    this._rankX  = rankX;
    this._nameX  = nameX;
    this._flagX  = flagX;
    this._scoreX = scoreX;
    this._rowH   = rowH;

    const hStyle = {
      fontSize: `${11 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold',
      color: '#7722aa',
    };
    this.add.text(rankX,  headerY, '#',       hStyle).setOrigin(0.5).setDepth(4);
    this.add.text(nameX,  headerY, 'Player',  hStyle).setOrigin(0.5).setDepth(4);
    this.add.text(flagX,  headerY, 'Country', hStyle).setOrigin(0.5).setDepth(4);
    this.add.text(scoreX, headerY, 'Score',   hStyle).setOrigin(1, 0.5).setDepth(4);

    const divGfx = this.add.graphics().setDepth(4);
    divGfx.lineStyle(1.5 * dpr, 0xbb88ee, 0.6);
    divGfx.lineBetween(cardX - cardW / 2 + 10 * sd, headerY + 14 * sd, cardX + cardW / 2 - 10 * sd, headerY + 14 * sd);

    // ── Scrollable list area ──────────────────────────────────────
    const listTop    = headerY + 20 * sd;
    const listBottom = cardBotY - 52 * sd;
    const listH      = listBottom - listTop;

    this._listTop    = listTop;
    this._listBottom = listBottom;
    this._listH      = listH;
    this._cardX      = cardX;
    this._cardW      = cardW;

    const maskShape = this.make.graphics({ add: false });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(cardX - cardW / 2, listTop, cardW, listH);

    this._rowContainer = this.add.container(0, listTop).setDepth(4);
    this._rowContainer.setMask(new Phaser.Display.Masks.GeometryMask(this, maskShape));

    // Drag-to-scroll
    this.input.on('pointerdown', (p) => {
      if (p.y < listTop || p.y > listBottom) return;
      this._isDragging  = true;
      this._dragStartY  = p.y;
      this._dragScrollY = this._scrollY;
    });
    this.input.on('pointermove', (p) => {
      if (!this._isDragging) return;
      this._setScroll(this._dragScrollY - (p.y - this._dragStartY));
    });
    this.input.on('pointerup',   () => { this._isDragging = false; });
    this.input.on('wheel', (p, objs, dx, dy) => { this._setScroll(this._scrollY + dy * 0.5); });

    // ── Footer — Back button ──────────────────────────────────────
    const footerMidY = cardBotY - 26 * sd;
    const btnH2      = 38 * sd;
    const halfBtnW   = Math.min(120 * sd, cardW * 0.36);
    const backX      = cardX;

    const backGfx  = this.add.graphics().setDepth(5);
    const drawBack = (color) => {
      backGfx.clear();
      backGfx.fillStyle(color, 1);
      backGfx.fillRoundedRect(backX - halfBtnW / 2, footerMidY - btnH2 / 2, halfBtnW, btnH2, btnH2 / 2);
    };
    drawBack(0x5533aa);
    this.add.text(backX, footerMidY, '← Back', {
      fontSize: `${12 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold',
      color: '#ffffff', stroke: '#220066', strokeThickness: 2 * dpr,
    }).setOrigin(0.5).setDepth(6);
    const backHit = this.add.rectangle(backX, footerMidY, halfBtnW, btnH2)
      .setInteractive({ useHandCursor: true }).setDepth(6);
    backHit.on('pointerover',  () => drawBack(0x7755cc));
    backHit.on('pointerout',   () => drawBack(0x5533aa));
    backHit.on('pointerdown',  () => {
      this.cameras.main.fadeOut(250, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('LeaderboardScene');
        this.scene.start('HomeScene');
      });
    });

    // "Go to Yours" button — left of Back, shown when player rank is off-screen
    const goX   = cardX - halfBtnW - 8 * sd;
    this._goGfx = this.add.graphics().setDepth(5);
    this._goTxt = this.add.text(goX, footerMidY, '📍 Go to Yours', {
      fontSize: `${11 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold',
      color: '#ffffff', stroke: '#5511aa', strokeThickness: 2 * dpr,
    }).setOrigin(0.5).setDepth(6).setVisible(false);
    this._goHit      = this.add.rectangle(goX, footerMidY, halfBtnW, btnH2)
      .setDepth(6).setVisible(false);

    this._footerMidY = footerMidY;
    this._halfBtnW   = halfBtnW;
    this._btnH2      = btnH2;
    this._goX        = goX;

    // ── Loading text ──────────────────────────────────────────────
    this._loadingTxt = this.add.text(cardX, listTop + listH / 2, 'Loading...', {
      fontSize: `${15 * sd}px`, fontFamily: FONT,
      color: '#7722aa',
    }).setOrigin(0.5).setDepth(5);

    this.cameras.main.fadeIn(300, 0, 0, 0);
    this._init();
  }

  async _init() {
    const player = await getOrCreatePlayer();
    this._playerUUID = player.uuid;
    this._player = player;

    if (!player.name) {
      // Check if user already has scores submitted under a placeholder name
      const hasScores = await this._checkIfPlayerHasScores();
      if (hasScores) {
        // Show claim banner instead of immediate onboarding modal
        this._showClaimBanner();
        this._loadScores();
      } else {
        // Brand-new user — show full onboarding
        this._showOnboarding();
      }
    } else {
      this._loadScores();
    }
  }

  async _checkIfPlayerHasScores() {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const foodsRef  = doc(db, 'leaderboard_foods', this._playerUUID);
      const foodsSnap = await getDoc(foodsRef);
      return foodsSnap.exists();
    } catch (e) {
      console.warn('[Leaderboard] _checkIfPlayerHasScores failed:', e);
      return false;
    }
  }

  // ── Claim banner for unregistered users who already have scores ───────────
  _showClaimBanner() {
    const { _W: W, _sd: sd, _dpr: dpr } = this;

    const bannerContainer = this.add.container(0, 0).setDepth(15);
    this._claimBanner = bannerContainer;

    const bannerW = Math.min(500 * sd, W * 0.9);
    const bannerH = 120 * sd;
    const bannerY = 100 * sd;

    // Banner background — purple theme
    const bannerGfx = this.add.graphics();
    bannerGfx.fillStyle(0xfcf8ff, 0.98);
    bannerGfx.fillRoundedRect(W / 2 - bannerW / 2, bannerY, bannerW, bannerH, 14);
    bannerGfx.lineStyle(3 * dpr, 0x9944cc);
    bannerGfx.strokeRoundedRect(W / 2 - bannerW / 2, bannerY, bannerW, bannerH, 14);
    bannerGfx.fillStyle(0x9944cc, 1);
    bannerGfx.fillRoundedRect(W / 2 - bannerW / 2, bannerY, bannerW, 8 * sd, { tl: 14, tr: 14, bl: 0, br: 0 });
    bannerContainer.add(bannerGfx);

    const titleTxt = this.add.text(W / 2, bannerY + 24 * sd, '✨ You\'re on the leaderboard!', {
      fontSize: `${15 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold italic',
      color: '#9933cc',
    }).setOrigin(0.5);
    bannerContainer.add(titleTxt);

    const descTxt = this.add.text(W / 2, bannerY + 52 * sd,
      `Currently listed as "Player-${this._playerUUID.slice(-4)}". Claim your spot with your real name!`, {
      fontSize: `${11 * sd}px`, fontFamily: FONT, color: '#886677',
      align: 'center', wordWrap: { width: bannerW * 0.9 },
    }).setOrigin(0.5);
    bannerContainer.add(descTxt);

    // Claim button
    const btnW = 150 * sd;
    const btnH = 36 * sd;
    const btnY = bannerY + 88 * sd;
    const btnGfx = this.add.graphics();
    const drawBtn = (color) => {
      btnGfx.clear();
      btnGfx.fillStyle(color, 1);
      btnGfx.fillRoundedRect(W / 2 - btnW / 2, btnY - btnH / 2, btnW, btnH, btnH / 2);
    };
    drawBtn(0x9944cc);
    const btnTxt = this.add.text(W / 2, btnY, '🌍  Claim Now', {
      fontSize: `${13 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold',
      color: '#ffffff', stroke: '#5511aa', strokeThickness: 2 * dpr,
    }).setOrigin(0.5);
    bannerContainer.add([btnGfx, btnTxt]);

    const btnHit = this.add.rectangle(W / 2, btnY, btnW, btnH)
      .setInteractive({ useHandCursor: true });
    bannerContainer.add(btnHit);
    btnHit.on('pointerover', () => drawBtn(0xbb77ff));
    btnHit.on('pointerout',  () => drawBtn(0x9944cc));
    btnHit.on('pointerdown', () => {
      bannerContainer.destroy(true);
      this._claimBanner = null;
      this._showOnboarding();
    });
  }

  // ── Onboarding card ───────────────────────────────────────────────────────
  _showOnboarding() {
    this._onboardingOpen = true;
    const { _W: W, _H: H, _sd: sd, _dpr: dpr } = this;

    const container = this.add.container(0, 0).setDepth(20);

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.55).setInteractive();
    container.add(overlay);

    const cardW = Math.min(300 * sd, W * 0.88);
    const cardH = 290 * sd;
    const cardY = H / 2;

    const cardGfx = this.add.graphics();
    cardGfx.fillStyle(0xfcf8ff, 0.98);
    cardGfx.fillRoundedRect(W / 2 - cardW / 2, cardY - cardH / 2, cardW, cardH, 18);
    cardGfx.fillStyle(0x9944cc, 1);
    cardGfx.fillRoundedRect(W / 2 - cardW / 2, cardY - cardH / 2, cardW, 48 * sd, 18);
    cardGfx.fillRect(W / 2 - cardW / 2, cardY - cardH / 2 + 24 * sd, cardW, 24 * sd);
    cardGfx.lineStyle(2 * dpr, 0xbb88ee);
    cardGfx.strokeRoundedRect(W / 2 - cardW / 2, cardY - cardH / 2, cardW, cardH, 18);
    container.add(cardGfx);

    const titleTxt = this.add.text(W / 2, cardY - cardH / 2 + 24 * sd, '🌍  Join the Leaderboard', {
      fontSize: `${16 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold italic',
      color: '#ffffff', stroke: '#5511aa', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);
    container.add(titleTxt);

    const nameLbl = this.add.text(W / 2, cardY - cardH / 2 + 68 * sd, 'Your name:', {
      fontSize: `${13 * sd}px`, fontFamily: FONT, color: '#7722aa',
    }).setOrigin(0.5);
    container.add(nameLbl);

    // HTML name input
    const inputEl = document.createElement('input');
    inputEl.type        = 'text';
    inputEl.maxLength   = 16;
    inputEl.placeholder = 'Enter your name';
    inputEl.style.cssText = `
      position: fixed;
      left: 50%; top: ${(cardY - cardH / 2 + 86 * sd) / dpr}px;
      transform: translateX(-50%);
      width: ${(cardW * 0.72) / dpr}px;
      padding: 7px 12px;
      font-size: ${14 * sd / dpr}px;
      font-family: Georgia, serif;
      border-radius: 10px;
      border: 2px solid #bb88ee;
      background: #f8f0ff;
      color: #3a1a5e;
      outline: none;
      text-align: center;
      z-index: 9999;
    `;
    document.body.appendChild(inputEl);
    setTimeout(() => inputEl.focus(), 100);

    const countryLbl = this.add.text(W / 2, cardY - cardH / 2 + 132 * sd, 'Your country:', {
      fontSize: `${13 * sd}px`, fontFamily: FONT, color: '#7722aa',
    }).setOrigin(0.5);
    container.add(countryLbl);

    // Country picker
    let countryIdx = Math.max(0, COUNTRIES.findIndex(c => c[0] === 'IN'));
    const pickerY  = cardY - cardH / 2 + 160 * sd;
    const pickerW  = cardW * 0.66;
    const arrowSz  = 30 * sd;

    const pickerGfx = this.add.graphics();
    pickerGfx.fillStyle(0xf8f0ff, 1);
    pickerGfx.fillRoundedRect(W / 2 - pickerW / 2, pickerY - 18 * sd, pickerW, 36 * sd, 10);
    pickerGfx.lineStyle(1.5 * dpr, 0xbb88ee);
    pickerGfx.strokeRoundedRect(W / 2 - pickerW / 2, pickerY - 18 * sd, pickerW, 36 * sd, 10);
    container.add(pickerGfx);

    const pickerTxt = this.add.text(W / 2, pickerY,
      `${countryFlag(COUNTRIES[countryIdx][0])}  ${COUNTRIES[countryIdx][1]}`, {
      fontSize: `${13 * sd}px`, fontFamily: FONT, color: '#3a1a5e',
    }).setOrigin(0.5);
    container.add(pickerTxt);

    const prevHit  = this.add.rectangle(W / 2 - pickerW / 2 - arrowSz / 2 - 4 * sd, pickerY, arrowSz, arrowSz).setInteractive({ useHandCursor: true });
    const nextHit  = this.add.rectangle(W / 2 + pickerW / 2 + arrowSz / 2 + 4 * sd, pickerY, arrowSz, arrowSz).setInteractive({ useHandCursor: true });
    const prevArrow = this.add.text(W / 2 - pickerW / 2 - arrowSz / 2 - 4 * sd, pickerY, '◀', { fontSize: `${16 * sd}px`, fontFamily: FONT, color: '#9944cc' }).setOrigin(0.5);
    const nextArrow = this.add.text(W / 2 + pickerW / 2 + arrowSz / 2 + 4 * sd, pickerY, '▶', { fontSize: `${16 * sd}px`, fontFamily: FONT, color: '#9944cc' }).setOrigin(0.5);
    container.add([prevHit, nextHit, prevArrow, nextArrow]);

    prevHit.on('pointerdown', () => {
      countryIdx = (countryIdx - 1 + COUNTRIES.length) % COUNTRIES.length;
      pickerTxt.setText(`${countryFlag(COUNTRIES[countryIdx][0])}  ${COUNTRIES[countryIdx][1]}`);
    });
    nextHit.on('pointerdown', () => {
      countryIdx = (countryIdx + 1) % COUNTRIES.length;
      pickerTxt.setText(`${countryFlag(COUNTRIES[countryIdx][0])}  ${COUNTRIES[countryIdx][1]}`);
    });

    // Join button
    const joinY   = cardY + cardH / 2 - 38 * sd;
    const joinW   = cardW * 0.55;
    const joinH   = 44 * sd;
    const joinGfx = this.add.graphics();
    const drawJoin = (color) => {
      joinGfx.clear();
      joinGfx.fillStyle(color, 1);
      joinGfx.fillRoundedRect(W / 2 - joinW / 2, joinY - joinH / 2, joinW, joinH, joinH / 2);
    };
    drawJoin(0x9944cc);
    const joinTxt = this.add.text(W / 2, joinY, '🌍  Join', {
      fontSize: `${15 * sd}px`, fontFamily: FONT_BOLD, fontStyle: 'bold',
      color: '#ffffff', stroke: '#5511aa', strokeThickness: 2 * dpr,
    }).setOrigin(0.5);
    container.add([joinGfx, joinTxt]);

    const joinHit = this.add.rectangle(W / 2, joinY, joinW, joinH).setInteractive({ useHandCursor: true });
    container.add(joinHit);

    joinHit.on('pointerover', () => drawJoin(0xbb77ff));
    joinHit.on('pointerout',  () => drawJoin(0x9944cc));
    joinHit.on('pointerdown', async () => {
      const rawName = inputEl.value.trim();
      const name    = rawName.length > 0 ? rawName.slice(0, 16) : 'Anonymous';
      const code    = COUNTRIES[countryIdx][0];
      if (document.body.contains(inputEl)) document.body.removeChild(inputEl);
      await savePlayerProfile(name, code);
      // Refresh player object after registration
      this._player = await getOrCreatePlayer();
      container.destroy(true);
      this._onboardingOpen = false;
      this._loadScores();
    });
  }

  // ── Load scores ───────────────────────────────────────────────────────────
  async _loadScores() {
    if (this._loadingTxt) this._loadingTxt.setVisible(true);
    this._clearRows();
    this._setGoToYoursVisible(false);

    const [rows, rank] = await Promise.all([
      getTopScores(this._tab, 100),
      getPlayerRank(this._tab, this._playerUUID),
    ]);

    this._playerRank = rank;
    if (this._loadingTxt) this._loadingTxt.setVisible(false);
    this._renderRows(rows);
  }

  _clearRows() {
    if (this._rowContainer) this._rowContainer.removeAll(true);
    this._rows    = [];
    this._scrollY = 0;
  }

  _renderRows(rows) {
    const { _sd: sd, _dpr: dpr, _rowH: rowH, _cardX: cardX, _cardW: cardW } = this;
    const rankX  = this._rankX;
    const nameX  = this._nameX;
    const flagX  = this._flagX;
    const scoreX = this._scoreX;

    const baseStyle = { fontFamily: FONT, fontSize: `${12 * sd}px`, color: '#3a1a5e' };
    const youStyle  = { fontFamily: FONT_BOLD, fontStyle: 'bold', fontSize: `${12 * sd}px`, color: '#7722aa' };
    const medalColors = ['#f5c518', '#aaaaaa', '#cd7f32'];

    rows.forEach((row, i) => {
      const isYou = row.uuid === this._playerUUID;
      const isPlaceholder = row.name.startsWith('Player-');
      const y     = i * rowH + rowH / 2;
      const style = isYou ? youStyle : baseStyle;

      const rowBg = this.add.rectangle(cardX, y, cardW - 16 * sd, rowH * 0.9,
        isYou ? 0xeeddff : (i % 2 === 0 ? 0xffffff : 0xf8f0ff),
        isYou ? 0.9 : 0.6
      );
      if (isYou) rowBg.setStrokeStyle(1.5 * dpr, 0xbb88ee);
      this._rowContainer.add(rowBg);

      const rankLabel = i < 3 ? ['🥇','🥈','🥉'][i] : `${row.rank}`;
      const rankStyle = i < 3
        ? { fontFamily: FONT, fontSize: `${14 * sd}px`, color: medalColors[i] }
        : style;

      let displayName;
      if (isYou && isPlaceholder) {
        displayName = `👤 ${row.name} (Unclaimed)`;
      } else if (isYou) {
        displayName = `${row.name.slice(0, 12)} (You)`;
      } else {
        displayName = row.name.slice(0, 14);
      }
      const scoreLabel  = this._tab === 'height' ? `${row.score}` : `${row.score}`;

      const rankTxt  = this.add.text(rankX,  y, rankLabel,                    rankStyle).setOrigin(0.5);
      const nameTxt  = this.add.text(nameX,  y, displayName,                  style).setOrigin(0.5);
      const flagTxt  = this.add.text(flagX,  y, countryFlag(row.countryCode), style).setOrigin(0.5);
      const scoreTxt = this.add.text(scoreX, y, scoreLabel,                   style).setOrigin(1, 0.5);

      this._rowContainer.add([rankTxt, nameTxt, flagTxt, scoreTxt]);
      this._rows.push({ y, isYou });
    });

    const totalH    = rows.length * rowH;
    this._maxScroll = Math.max(0, totalH - this._listH);

    const visibleRows = Math.floor(this._listH / rowH);
    if (this._playerRank && this._playerRank > visibleRows) {
      this._setupGoToYours();
    }
  }

  _setupGoToYours() {
    const { _sd: sd, _dpr: dpr, _halfBtnW: halfBtnW, _btnH2: btnH2, _footerMidY: footerMidY, _goX: goX } = this;

    this._goGfx.clear();
    this._goGfx.fillStyle(0xddaa00, 1);
    this._goGfx.fillRoundedRect(goX - halfBtnW / 2, footerMidY - btnH2 / 2, halfBtnW, btnH2, btnH2 / 2);

    this._goTxt.setVisible(true);
    this._goHit.setVisible(true).setInteractive({ useHandCursor: true });
    this._goHit.off('pointerdown');
    this._goHit.on('pointerdown', () => {
      const youRow = this._rows.find(r => r.isYou);
      if (!youRow) return;
      const target = Phaser.Math.Clamp(youRow.y - this._listH / 2, 0, this._maxScroll);
      this.tweens.add({
        targets: this, _scrollY: target, duration: 500,
        ease: 'Cubic.easeInOut', onUpdate: () => this._applyScroll(),
      });
    });
  }

  _setGoToYoursVisible(v) {
    if (this._goTxt) this._goTxt.setVisible(v);
    if (this._goHit) { this._goHit.setVisible(v); if (!v) this._goHit.removeInteractive(); }
    if (this._goGfx) this._goGfx.clear();
  }

  _setScroll(val) {
    this._scrollY = Phaser.Math.Clamp(val, 0, this._maxScroll);
    this._applyScroll();
  }

  _applyScroll() {
    if (this._rowContainer) {
      this._rowContainer.setY(this._listTop - this._scrollY);
    }
  }
}
